// Local types for the academics-domain portal screens. We intentionally keep
// these decoupled from `@bbl/db` because the database package targets the
// Cloudflare worker runtime and pulling its types into the Vite bundle breaks
// the build. The shapes here mirror the JSON responses returned by the API.

export type DayOfWeek = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

export interface BatchScheduleSlot {
  day: DayOfWeek;
  /** HH:MM in 24h. */
  start: string;
  /** HH:MM in 24h. */
  end: string;
  /** Hybrid batches: whether this class meets online or at the centre. */
  mode?: 'online' | 'offline';
}

export type BatchMode = 'online' | 'offline' | 'hybrid';
export type BatchStatus = 'draft' | 'active' | 'completed' | 'archived';

/**
 * The list endpoint returns `schedule` as a JSON-encoded string (the DB column
 * type). The detail endpoint returns the same. We parse on read with
 * `parseSchedule` rather than assuming a particular shape from the wire.
 */
export interface BatchRecord {
  id: string;
  name: string;
  courseId: string;
  teacherId: string;
  nalimId: string | null;
  mode: BatchMode;
  startDate: string;
  endDate: string | null;
  /** JSON-encoded `BatchScheduleSlot[]`. */
  schedule: string;
  capacity: number;
  location: string | null;
  notes: string | null;
  status: BatchStatus;
  createdAt: string;
  updatedAt: string;
}

export const parseSchedule = (raw: string | null | undefined): BatchScheduleSlot[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is BatchScheduleSlot => {
      if (!s || typeof s !== 'object') return false;
      const slot = s as Record<string, unknown>;
      return (
        typeof slot.day === 'string' &&
        typeof slot.start === 'string' &&
        typeof slot.end === 'string'
      );
    });
  } catch {
    return [];
  }
};

export interface CourseSummary {
  id: string;
  slug: string;
  name: string;
  program: string;
  description: string | null;
  durationWeeksMin: number;
  durationWeeksMax: number;
  sessionsPerWeek: number;
  sessionMinutes: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LessonSummary {
  id: string;
  courseId: string;
  orderIndex: number;
  title: string;
  description: string | null;
  expectedDurationMinutes: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CourseWithLessons extends CourseSummary {
  lessons: LessonSummary[];
}

export type AttendanceStatus = 'present' | 'absent' | 'late' | 'excused';

/**
 * The status of a roster row in working (client) state. `'unmarked'` is a
 * CLIENT-ONLY value used while a non-finalized day has students no one has
 * touched yet — it is never persisted. Only the four real `AttendanceStatus`
 * values ever reach the wire/DB; the submit gate guarantees no `'unmarked'`
 * row is ever sent.
 */
export type WorkingStatus = AttendanceStatus | 'unmarked';

export interface AttendanceDay {
  id: string;
  date: string;
  batchId: string;
  sessionId: string | null;
  finalizedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AttendanceRecord {
  id: string;
  dayId: string;
  studentId: string;
  status: AttendanceStatus;
  markedBy: string;
  markedAt: string;
  notes: string | null;
}

export type WeeklyReportStatus = 'draft' | 'submitted' | 'approved' | 'changes_requested';

export interface WeeklyReport {
  id: string;
  batchId: string;
  weekStartDate: string;
  teacherId: string;
  summary: string;
  challenges: string | null;
  upcoming: string | null;
  studentNotes: string | null;
  status: WeeklyReportStatus;
  submittedAt: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MeetingReport {
  id: string;
  batchId: string | null;
  householdId: string | null;
  meetingAt: string;
  summary: string;
  /** Already parsed by the API. */
  attendees: string[];
  followUps: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ResourceRecord {
  id: string;
  batchId: string | null;
  courseId: string | null;
  title: string;
  description: string | null;
  r2Key: string;
  mimeType: string;
  sizeBytes: number;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
}

export type ProgressStatus = 'not_started' | 'in_progress' | 'completed' | 'needs_review';

export interface ProgressEntryRow {
  lessonId: string;
  lessonTitle: string;
  orderIndex: number;
  courseId: string;
  courseName: string;
  status: ProgressStatus;
  markedAt: string;
  notes: string | null;
}

export const BATCH_MODE_OPTIONS: ReadonlyArray<{ value: BatchMode; label: string }> = [
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
  { value: 'hybrid', label: 'Hybrid' },
];

export const BATCH_STATUS_FILTERS: ReadonlyArray<{
  value: 'all' | BatchStatus;
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

export const DAY_OF_WEEK_OPTIONS: ReadonlyArray<{ value: DayOfWeek; label: string }> = [
  { value: 'sun', label: 'Sunday' },
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
];

export const DAY_OF_WEEK_LABEL: Record<DayOfWeek, string> = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
};

export const ATTENDANCE_STATUS_OPTIONS: ReadonlyArray<{
  value: AttendanceStatus;
  label: string;
}> = [
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
  { value: 'late', label: 'Late' },
  { value: 'excused', label: 'Excused' },
];

export const PROGRESS_STATUS_OPTIONS: ReadonlyArray<{
  value: ProgressStatus;
  label: string;
}> = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'needs_review', label: 'Needs review' },
];

export const PROGRAM_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'quran-reading', label: 'Qur’an reading' },
  { value: 'tajweed', label: 'Tajweed' },
  { value: 'hifz', label: 'Hifz' },
  { value: 'adult-beginners', label: 'Adult beginners' },
];

export const programLabel = (slug: string): string => {
  const found = PROGRAM_OPTIONS.find((o) => o.value === slug);
  return found ? found.label : slug;
};

/**
 * Compute the YYYY-MM-DD of the most recent Monday at or before `date` in the
 * user's local timezone. Weekly reports anchor on Monday in the API contract.
 */
export const startOfWeekMonday = (date: Date = new Date()): string => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0 = Sunday, 1 = Monday, ...
  const delta = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + delta);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

/** Pretty-print bytes for resource listings. */
export const humanBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};
